import { createReducer, on } from "@ngrx/store";
import { DocViewerState } from "../doc-viewer.state";
import { AnnotationEditorState } from "./annotation-editor.state";
import { AnnotationStatus } from "@models/annotation-status.enum";
import { annotationEditorActions, currentAnnotationActions } from "./annotation-editor.actions";
import { cloneDeep } from "lodash-es";
import { LifeCycleEdit } from "src/app/components/review/redaction-annotation/life-cycle-edit.enum";
import { ComparisonUtility } from "@utilities/comparison.utility";
import { highlightColorIdToHighlightColorMap } from "@models/mappers/maps/highlight-color-id-to-highlight-color.map";

export const initialState: AnnotationEditorState = {
    pageNumber: 0,
    totalPages: 0,
    fileIdList: {},
    currentAnnotationId: 0,
    hasUnsavedAnnotationChanges: false,
    annotations: [],
    colors: [],
    isProcessing: false,
    status: AnnotationStatus.Unknown,
    annotationText: '',
    annotationColorId: '1a3bcf6e-e844-e911-a9db-124a916a6f54'
}

export const annotationEditorReducer = createReducer<DocViewerState['annotationEditor']>(
    initialState,
    on(annotationEditorActions.initialize, (_state, { status, totalPages }) => {
        const clone = cloneDeep(_state);
        clone.status = status;
        clone.isProcessing = false;
        clone.totalPages = totalPages;
        clone.pageNumber = 1;
        return clone;
    }),
    on(annotationEditorActions.annotationColorsLoaded, (_state, { colors }) => {
        const clone = cloneDeep(_state);
        clone.colors = colors;
        return clone;
    }),
    on(annotationEditorActions.annotationsLoaded, (_state, { annotations }) => {
        const clone = cloneDeep(_state);
        clone.annotations = cloneDeep(annotations);
        return clone;
    }),
    on(annotationEditorActions.goToPage, (_state, { pageNum }) => {
        const clone = cloneDeep(_state);
        clone.pageNumber = pageNum;
        return clone;
    }),
    on(annotationEditorActions.setFileIdList, (_state, { items }) => {
        const clone = cloneDeep(_state);
        clone.fileIdList = items;
        return clone;
    }),
    on(annotationEditorActions.processingStarted, (_state) => {
        const clone = cloneDeep(_state);
        clone.isProcessing = true;
        return clone;
    }),
    on(annotationEditorActions.processingStopped, (_state, { status }) => {
        const clone = cloneDeep(_state);
        clone.isProcessing = false;
        clone.status = status;
        return clone;
    }),
    on(annotationEditorActions.markEditorDirty, (_state) => {
        const clone = cloneDeep(_state);
        clone.hasUnsavedAnnotationChanges = true;
        return clone;
    }),
    on(annotationEditorActions.markEditorClean, (_state) => {
        const clone = cloneDeep(_state);
        clone.hasUnsavedAnnotationChanges = false;
        return clone;
    }),
    on(annotationEditorActions.annotationStatusLoaded, (_state, { status }) => {
        const clone = cloneDeep(_state);
        clone.status = status;
        return clone;
    }),
    on(annotationEditorActions.createImage, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = AnnotationStatus.ImagesCreationRequested;
        clone.isProcessing = true;
        return clone;
    }),
    on(annotationEditorActions.imageCreated, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = AnnotationStatus.HasImages;
        clone.isProcessing = false;
        return clone;
    }),
    on(annotationEditorActions.createAnnotatedPDF, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = AnnotationStatus.PdfCreationRequested;
        clone.isProcessing = true;
        clone.hasUnsavedAnnotationChanges = false;
        return clone;
    }),
    on(annotationEditorActions.annotatedPDFCreated, (_state) => {
        const clone = cloneDeep(_state);
        clone.status = AnnotationStatus.HasPdf;
        clone.isProcessing = false;
        return clone;
    }),
    on(annotationEditorActions.updateAnnotationsForCurrentPage, (_state, { annotations }) => {
        const clone = cloneDeep(_state);
        if (!ComparisonUtility.isEqual(clone.annotations, annotations)) {
            clone.hasUnsavedAnnotationChanges = true;
        }
        const updatedAnnotations = cloneDeep(annotations).map((updatedAnnotation) => {
            if (updatedAnnotation.status === LifeCycleEdit.Initialized) {
                updatedAnnotation.fileId = _state.fileIdList[_state.pageNumber];
                updatedAnnotation.text = _state.annotationText;
                updatedAnnotation.highlightColorId = _state.annotationColorId;
                updatedAnnotation.highlightColor = highlightColorIdToHighlightColorMap.get(_state.annotationColorId);
                updatedAnnotation.rank = _state.pageNumber;
                updatedAnnotation.status = LifeCycleEdit.Created;
            }
            return updatedAnnotation;
        });

        updatedAnnotations.forEach((updatedAnnotation) => {
            const existingIndex = clone.annotations.findIndex(existing => existing.clientId === updatedAnnotation.clientId);
            if (existingIndex >= 0) {
                clone.annotations[existingIndex] = updatedAnnotation;
            } else {
                clone.annotations.push(updatedAnnotation);
            }
        });
        return clone;
    }),
    on(annotationEditorActions.resetAnnotationsForCurrentDoc, (_state) => {
        const clone = cloneDeep(_state);
        clone.annotations = [];
        return clone;
    }),
    on(currentAnnotationActions.setCurrentAnnotation, (_state, { annotationId }) => {
        const clone = cloneDeep(_state);
        clone.currentAnnotationId = annotationId;
        return clone;
    }),
    on(currentAnnotationActions.updateAnnotation, (_state, { annotation }) => {
        const clone = cloneDeep(_state);
        const targetIndex = clone.annotations.findIndex(a => a.clientId === annotation.clientId);
        if (targetIndex !== -1) {
            const annotationClone = cloneDeep(annotation);
            if (annotationClone.status === LifeCycleEdit.Initialized)
                annotationClone.status = LifeCycleEdit.Created;
            else if (annotationClone.status != LifeCycleEdit.Created)
                annotationClone.status = LifeCycleEdit.Modified;
            clone.annotations[targetIndex] = annotationClone;
            clone.hasUnsavedAnnotationChanges = true;
            return clone;
        }
        return clone;
    }),
    on(currentAnnotationActions.deleteAnnotation, (_state, { annotationId }) => {
        const clone = cloneDeep(_state);
        const targetIndex = clone.annotations.findIndex(a => a.clientId === annotationId);
        if (targetIndex === -1)
            return clone;

        const annotation = clone.annotations[targetIndex];
        if (annotation.status === LifeCycleEdit.Created)
            clone.annotations.splice(targetIndex, 1);
        else
            clone.annotations[targetIndex].status = LifeCycleEdit.Deleted;
        clone.hasUnsavedAnnotationChanges = true;
        return clone;
    }),
    on(currentAnnotationActions.setAnnotationText, (_state, { text }) => {
        const clone = cloneDeep(_state);
        clone.annotationText = text;
        return clone;
    }),
    on(currentAnnotationActions.setColorId, (_state, { colorId }) => {
        const clone = cloneDeep(_state);
        clone.annotationColorId = colorId;
        return clone;
    }),
);
